import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { X, Plus, Trash2, FileText, Send, Save, IndianRupee } from 'lucide-react';

const fmt = v => `\u20B9${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Invoice Builder Modal — used by both Vendor Finance and Freelancer Earnings.
 * Vendor/freelancer picks an accepted Work Order, picks a milestone (or a
 * subset of rate-card line items), enters notes, and submits. Admin sees it
 * land in their Accounting → Invoices Inbox.
 */
export default function InvoiceBuilderModal({ open, onClose, workOrders, onCreated, editingInvoice }) {
  const { api } = useAuth();
  const [woId, setWoId] = useState(editingInvoice?.work_order_id || '');
  const [milestone, setMilestone] = useState(editingInvoice?.milestone || '');
  const [lineItems, setLineItems] = useState(editingInvoice?.line_items || []);
  const [notes, setNotes] = useState(editingInvoice?.notes || '');
  const [gstPercent, setGstPercent] = useState(editingInvoice?.gst_percent ?? 18);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Pick first accepted WO automatically when modal opens
  useEffect(() => {
    if (open && !woId && workOrders?.length) {
      setWoId(workOrders[0].id);
    }
  }, [open, woId, workOrders]);

  const wo = useMemo(() => workOrders?.find(w => w.id === woId), [workOrders, woId]);
  const milestones = wo?.payment_terms?.milestones || [];

  // When milestone changes → auto-fill ONE line item with the milestone's portion
  const pickMilestone = (mName) => {
    setMilestone(mName);
    if (!wo) return;
    const m = milestones.find(x => x.name === mName);
    if (m) {
      const amount = ((wo.total_amount || 0) * (m.percent || 0)) / 100;
      setLineItems([{
        description: `${wo.work_order_no} — ${m.name} (${m.percent}%)`,
        quantity: 1,
        rate: amount,
        amount: amount,
        wo_line_id: '',
      }]);
    }
  };

  // When WO changes → reset
  useEffect(() => {
    if (wo && !editingInvoice) {
      setMilestone('');
      setLineItems([]);
      setGstPercent(wo.tax?.gst_percent ?? 18);
    }
  }, [wo, editingInvoice]);

  const addLine = () => setLineItems(prev => [...prev, { description: '', quantity: 1, rate: 0, amount: 0 }]);
  const removeLine = i => setLineItems(prev => prev.filter((_, idx) => idx !== i));
  const updateLine = (i, k, v) => setLineItems(prev => prev.map((li, idx) => {
    if (idx !== i) return li;
    const next = { ...li, [k]: v };
    if (k === 'quantity' || k === 'rate') {
      next.amount = Number(next.quantity || 0) * Number(next.rate || 0);
    }
    return next;
  }));

  const subtotal = lineItems.reduce((s, li) => s + Number(li.amount || 0), 0);
  const gst = subtotal * (Number(gstPercent || 0) / 100);
  const total = subtotal + gst;

  const submit = async (asDraft = false) => {
    setErr('');
    if (!woId) { setErr('Please pick a Work Order'); return; }
    if (lineItems.length === 0) { setErr('Add at least one line item'); return; }
    if (lineItems.some(li => !li.description?.trim())) { setErr('Every line item needs a description'); return; }
    if (subtotal <= 0) { setErr('Invoice total must be greater than 0'); return; }
    setSaving(true);
    try {
      const payload = {
        work_order_id: woId,
        milestone,
        line_items: lineItems,
        gst_percent: Number(gstPercent),
        notes,
        status: asDraft ? 'draft' : 'submitted',
      };
      const url = editingInvoice ? `/invoices/${editingInvoice.id}` : '/invoices';
      const method = editingInvoice ? 'put' : 'post';
      const { data } = await api[method](url, payload);
      if (onCreated) onCreated(data);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to save invoice');
    }
    setSaving(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="invoice-builder-modal">
      <div className="bg-zinc-950 border border-zinc-800 rounded-md w-full max-w-3xl max-h-[90vh] flex flex-col">
        <header className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-heading font-bold text-white">{editingInvoice ? 'Edit Invoice' : 'Raise Invoice'}</h3>
          </div>
          <button onClick={onClose} data-testid="invoice-modal-close" className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* WO picker */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Work Order *</label>
            <select value={woId} onChange={e => setWoId(e.target.value)} disabled={!!editingInvoice} data-testid="invoice-wo-select"
              className="w-full h-10 bg-zinc-900 border border-zinc-800 px-3 text-xs text-zinc-200 rounded-sm focus:outline-none focus:border-amber-500/60 disabled:opacity-60">
              <option value="">— Pick an accepted Work Order —</option>
              {workOrders?.map(w => (
                <option key={w.id} value={w.id}>
                  {w.work_order_no} · {w.project_name || w.scope_title} · {fmt(w.total_amount)}
                </option>
              ))}
            </select>
            {wo && (
              <p className="text-[10px] text-zinc-500 mt-1">
                {wo.scope_title} · Total Awarded: <span className="text-amber-400 font-mono">{fmt(wo.total_amount)}</span>
              </p>
            )}
          </div>

          {/* Milestone picker */}
          {milestones.length > 0 && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Milestone (optional — auto-fills line item)</label>
              <div className="flex gap-2 flex-wrap">
                {milestones.map(m => (
                  <button key={m.name} onClick={() => pickMilestone(m.name)} data-testid={`invoice-milestone-${m.name}`}
                    className={`text-[11px] px-3 py-1.5 rounded-sm transition-colors ${
                      milestone === m.name ? 'bg-amber-500 text-zinc-950 font-bold' : 'bg-zinc-900 border border-zinc-800 text-zinc-400 hover:border-amber-500/40'
                    }`}>
                    {m.name} · {m.percent}% · {fmt(((wo?.total_amount || 0) * (m.percent || 0)) / 100)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Line items table */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Line Items</label>
              <button onClick={addLine} data-testid="invoice-add-line" className="text-[11px] text-amber-400 hover:text-amber-300 flex items-center gap-1"><Plus className="w-3 h-3" /> Add line</button>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
              <table className="w-full text-[11px]">
                <thead className="bg-zinc-950 text-zinc-500">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-normal w-[55%]">Description</th>
                    <th className="text-right px-2 py-1.5 font-normal w-[10%]">Qty</th>
                    <th className="text-right px-2 py-1.5 font-normal w-[15%]">Rate</th>
                    <th className="text-right px-2 py-1.5 font-normal w-[15%]">Amount</th>
                    <th className="w-[5%]"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.length === 0 ? (
                    <tr><td colSpan="5" className="text-center text-zinc-600 py-6">No line items yet — pick a milestone above or click "+ Add line"</td></tr>
                  ) : lineItems.map((li, i) => (
                    <tr key={i} className="border-t border-zinc-800/50">
                      <td className="px-2 py-1.5"><input value={li.description} onChange={e => updateLine(i, 'description', e.target.value)} data-testid={`line-desc-${i}`} placeholder="e.g. Animation, 3 shots" className="w-full bg-zinc-950 border border-zinc-800 px-2 py-1 text-[11px] text-zinc-200 rounded-sm focus:outline-none focus:border-amber-500/60" /></td>
                      <td className="px-2 py-1.5"><input type="number" value={li.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} data-testid={`line-qty-${i}`} className="w-full bg-zinc-950 border border-zinc-800 px-2 py-1 text-[11px] text-zinc-200 text-right rounded-sm focus:outline-none focus:border-amber-500/60" /></td>
                      <td className="px-2 py-1.5"><input type="number" value={li.rate} onChange={e => updateLine(i, 'rate', e.target.value)} data-testid={`line-rate-${i}`} className="w-full bg-zinc-950 border border-zinc-800 px-2 py-1 text-[11px] text-zinc-200 text-right rounded-sm focus:outline-none focus:border-amber-500/60" /></td>
                      <td className="px-2 py-1.5 text-right text-amber-400 font-mono">{fmt(li.amount)}</td>
                      <td className="px-2 py-1.5"><button onClick={() => removeLine(i)} data-testid={`line-remove-${i}`} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes + GST */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Notes / payment instructions (optional)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} data-testid="invoice-notes" rows={3}
                placeholder="e.g. Payment via NEFT to bank account on file. Ref: Project Alpha S01E03."
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-xs text-zinc-200 rounded-sm focus:outline-none focus:border-amber-500/60 resize-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">GST %</label>
              <input type="number" value={gstPercent} onChange={e => setGstPercent(e.target.value)} data-testid="invoice-gst-percent"
                className="w-full h-10 bg-zinc-900 border border-zinc-800 px-3 text-xs text-zinc-200 rounded-sm focus:outline-none focus:border-amber-500/60" />
            </div>
          </div>

          {/* Totals strip */}
          <div className="bg-zinc-900 border border-amber-500/30 rounded-sm p-3 space-y-1 text-xs" data-testid="invoice-totals">
            <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span className="font-mono">{fmt(subtotal)}</span></div>
            <div className="flex justify-between text-zinc-400"><span>GST ({gstPercent}%)</span><span className="font-mono">{fmt(gst)}</span></div>
            <div className="flex justify-between text-amber-400 font-bold border-t border-zinc-800 pt-1 text-sm"><span>Total Due</span><span className="font-mono">{fmt(total)}</span></div>
          </div>

          {err && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-xs px-3 py-2 rounded-sm" data-testid="invoice-error">{err}</div>}
        </div>

        <footer className="px-5 py-3 border-t border-zinc-800 flex items-center justify-end gap-2">
          <button onClick={onClose} data-testid="invoice-cancel" className="h-9 px-4 text-xs text-zinc-400 hover:text-white">Cancel</button>
          <button onClick={() => submit(true)} disabled={saving} data-testid="invoice-save-draft" className="h-9 px-4 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs rounded-sm flex items-center gap-2 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> Save as Draft
          </button>
          <button onClick={() => submit(false)} disabled={saving} data-testid="invoice-submit" className="h-9 px-5 bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs font-bold rounded-sm flex items-center gap-2 disabled:opacity-50">
            <Send className="w-3.5 h-3.5" /> {saving ? 'Sending...' : 'Submit to Accounts'}
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * Compact invoice list for vendor/freelancer dashboards. Shows status,
 * total, milestone, and a tiny status timeline.
 */
export function InvoiceList({ invoices, onEdit, onDelete }) {
  if (!invoices?.length) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6 text-center" data-testid="invoice-list-empty">
        <IndianRupee className="w-6 h-6 text-zinc-700 mx-auto mb-2" />
        <p className="text-xs text-zinc-500">No invoices yet</p>
        <p className="text-[10px] text-zinc-600 mt-1">Click "Raise Invoice" to bill an accepted Work Order milestone.</p>
      </div>
    );
  }
  const statusColor = {
    draft: 'bg-zinc-700 text-zinc-300',
    submitted: 'bg-amber-500/20 text-amber-400',
    approved: 'bg-blue-500/20 text-blue-400',
    paid: 'bg-emerald-500/20 text-emerald-400',
    rejected: 'bg-red-500/20 text-red-400',
    on_hold: 'bg-orange-500/20 text-orange-400',
  };
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden" data-testid="invoice-list">
      <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center justify-between">
        <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">My Invoices ({invoices.length})</h3>
      </div>
      <div className="divide-y divide-zinc-800/30">
        {invoices.map(inv => (
          <div key={inv.id} className="px-4 py-3 hover:bg-zinc-900/40" data-testid={`invoice-row-${inv.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-white font-mono">{inv.invoice_no}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${statusColor[inv.status] || 'bg-zinc-700 text-zinc-300'}`} data-testid={`invoice-status-${inv.id}`}>
                    {inv.status?.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-[10px] text-zinc-500 mt-0.5 truncate">
                  {inv.work_order_no} · {inv.project_name || '—'} {inv.milestone ? `· ${inv.milestone}` : ''}
                </p>
                {inv.paid_at && <p className="text-[9px] text-emerald-400/80 mt-0.5">Paid {new Date(inv.paid_at).toLocaleDateString()} {inv.payment_ref ? `· Ref: ${inv.payment_ref}` : ''}</p>}
              </div>
              <div className="text-right ml-4">
                <p className="text-sm font-mono text-amber-400 font-bold">{fmt(inv.total_amount)}</p>
                <p className="text-[9px] text-zinc-600">incl. {inv.gst_percent}% GST</p>
                {inv.status === 'draft' && (
                  <div className="flex gap-2 mt-1 justify-end">
                    {onEdit && <button onClick={() => onEdit(inv)} data-testid={`invoice-edit-${inv.id}`} className="text-[10px] text-amber-400 hover:text-amber-300">Edit</button>}
                    {onDelete && <button onClick={() => onDelete(inv)} data-testid={`invoice-delete-${inv.id}`} className="text-[10px] text-red-400 hover:text-red-300">Delete</button>}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
